import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import ScriptureHeader from '../components/ScriptureHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MINISTRY_BANNER = 'https://static.prod-images.emergentagent.com/jobs/9bd4de2a-403c-4211-ba6a-7e7952c06d35/images/0f840a9da0fc7fdcf5f799cedc92d5d0049c007d763102e1d4304916e6d82131.png';

export default function PrayerPage() {
  const { t } = useTranslation();
  const [publicPrayers, setPublicPrayers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    request: '',
    is_public: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchPublicPrayers();
  }, []);

  const fetchPublicPrayers = async () => {
    try {
      const response = await axios.get(`${API}/prayers?public_only=true`);
      setPublicPrayers(response.data);
    } catch (error) {
      console.error('Error fetching prayers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/prayers`, formData);
      setSubmitted(true);
      toast.success(t('prayer.prayingForYou'));
      if (formData.is_public) {
        fetchPublicPrayers();
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-cream pt-24 flex items-center justify-center" data-testid="prayer-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto px-6"
        >
          <div className="w-20 h-20 bg-burgundy rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart size={40} className="text-white" />
          </div>
          <h1 className="font-serif text-3xl text-charcoal mb-4">{t('prayer.prayingForYou')}</h1>
          <p className="text-charcoal/70 mb-8">
            {t('prayer.requestReceived')}
          </p>
          <p className="text-sm text-charcoal/50 italic mb-8">
            "{t('prayer.verse')}" - {t('prayer.verseRef')}
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData({ name: '', email: '', request: '', is_public: false });
            }}
            className="bg-burgundy hover:bg-purple text-white"
          >
            {t('prayer.submitAnother')}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="prayer-page">
      {/* Scripture Header */}
      <ScriptureHeader page="prayer" />

      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 bg-burgundy rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart size={32} className="text-white" />
            </div>
            <span className="text-xs uppercase tracking-[0.2em] text-charcoal mb-4 block">{t('prayer.wereHereForYou')}</span>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">{t('prayer.title')}</h1>
            <p className="text-charcoal/70 max-w-2xl mx-auto">
              {t('prayer.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Prayer Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gold/10"
                data-testid="prayer-form"
              >
                <h2 className="font-serif text-2xl text-charcoal mb-6">{t('prayer.submitRequest')}</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label>{t('prayer.yourName')}</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder={t('prayer.anonymous')}
                      className="bg-cream border-gold/20 focus:border-gold"
                      data-testid="prayer-name"
                    />
                  </div>
                  
                  <div>
                    <Label>{t('prayer.emailOptional')}</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-cream border-gold/20 focus:border-gold"
                      data-testid="prayer-email"
                    />
                  </div>
                  
                  <div>
                    <Label>{t('prayer.yourRequest')}</Label>
                    <Textarea
                      value={formData.request}
                      onChange={(e) => setFormData({...formData, request: e.target.value})}
                      required
                      rows={5}
                      placeholder={t('prayer.requestPlaceholder')}
                      className="bg-cream border-gold/20 focus:border-gold"
                      data-testid="prayer-request"
                    />
                  </div>
                  
                  <label className="flex items-start gap-3 cursor-pointer p-3 bg-cream rounded-xl">
                    <Checkbox
                      checked={formData.is_public}
                      onCheckedChange={(checked) => setFormData({...formData, is_public: checked})}
                      data-testid="prayer-public"
                    />
                    <div>
                      <span className="text-sm text-charcoal block">{t('prayer.sharePublicly')}</span>
                      <span className="text-xs text-charcoal/50">{t('prayer.sharePubliclyNote')}</span>
                    </div>
                  </label>
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-burgundy hover:bg-purple text-white py-4"
                    data-testid="submit-prayer"
                  >
                    {isSubmitting ? t('prayer.submitting') : (
                      <>
                        <Send size={18} className="mr-2" /> {t('prayer.submit')}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>

            {/* Public Prayer Wall */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h2 className="font-serif text-2xl text-charcoal mb-6">{t('prayer.communityWall')}</h2>
              
              {publicPrayers.length > 0 ? (
                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                  {publicPrayers.map((prayer, i) => (
                    <div
                      key={prayer.id}
                      className="bg-white rounded-2xl p-5 border border-gold/10"
                      data-testid={`prayer-wall-${i}`}
                    >
                      <div className="flex items-start gap-3">
                        <Heart size={16} className="text-burgundy mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-charcoal/80 text-sm mb-2">{prayer.request}</p>
                          <p className="text-xs text-charcoal/50">
                            {prayer.name || t('prayer.anonymous')} • {new Date(prayer.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-8 border border-gold/10 text-center">
                  <Heart size={32} className="text-charcoal/20 mx-auto mb-3" />
                  <p className="text-charcoal/50 text-sm">{t('prayer.noPrayers')}</p>
                  <p className="text-charcoal/40 text-xs mt-1">{t('prayer.beFirst')}</p>
                </div>
              )}
              
              <div className="mt-6 p-4 bg-burgundy/5 rounded-xl text-center">
                <p className="text-sm text-burgundy italic">
                  "{t('prayer.prayForEachOther')}"
                </p>
                <p className="text-xs text-burgundy/70 mt-1">{t('prayer.verseRef')}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
